import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRouter } from "next/router";
import { ReactNode, useState } from "react";
import toast from "react-hot-toast";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import CommunicationOverrideModal from "@/components/booking/communicationOverrideModal";
import { MultiProviderAppointmentDrawer } from "@/components/booking/multiProviderAppointmentDrawer/multiProviderAppointmentDrawer";
import DropdownMenu, {
  DropdownMenuItems,
} from "@/components/common/dropdownMenu/dropdownMenu";
import {
  MenuHorizontalIcon,
  WarningCircleIcon,
} from "@/components/common/icons";
import { getCherryApplicationStatus } from "@/components/common/paymentPlans/cherryStatus";
import { useConfirm } from "@/components/providers/confirmProvider";
import TelehealthDetailsDialog from "@/components/serviceFlow/appointmentDetails/telehealth/telehealthDetailsDialog";
import { ERROR_PRIMARY, PRIMARY } from "@/config/mui/colorPalette";
import { useDeleteTelehealthAppointmentDetailsMutation } from "@/graphql/mutations/telehealth/deleteTelehealthAppointmentDetails.graphql.types";
import { useAppointmentChargeFeeAdditionalInfoLazyQuery } from "@/graphql/queries/appointment/appointmentDropdown.graphql.types";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import { useAppointmentStatusActions } from "@/hooks/serviceFlow/appointmentDropdown/useAppointmentStatusActions";
import useCancelAppointment from "@/hooks/serviceFlow/appointmentDropdown/useCancelAppointment";
import useChargeLateCancellationFee from "@/hooks/serviceFlow/appointmentDropdown/useChargeLateCancellationFee";
import { useChartReviewActions } from "@/hooks/serviceFlow/appointmentDropdown/useChartReviewActions";
import useCurrentAppointmentDropdownFragment from "@/hooks/serviceFlow/appointmentDropdown/useCurrentAppointmentDropdownFragment";
import { useEditApptComms } from "@/hooks/serviceFlow/appointmentDropdown/useEditApptComms";
import { usePackageSuggestionsDropdownMenuItems } from "@/hooks/serviceFlow/appointmentDropdown/usePackageSuggestionsActions";
import {
  ClientData,
  usePaymentPlansDropdownMenuItems,
} from "@/hooks/serviceFlow/appointmentDropdown/usePaymentPlansActions";
import { useStartReviewDropdownItem } from "@/hooks/serviceFlow/appointmentDropdown/useStartReviewDropdownItem";
import { useIsEligibleToRequestScripts } from "@/hooks/user/useIsEligibleToRequestScripts";
import {
  ClientCherryApplicationRecordStatus,
  InvoiceStatus,
  ReviewStatus,
  TelehealthType,
  AppointmentStatus,
} from "@/types";
import {
  isAppointmentActive,
  getIsLateCancellation,
} from "@/utils/appointment";
import { REMOVE_TELEHEALTH_DETAILS_CONFIRM_MODAL_CONTENT } from "@/utils/telehealth";
import useErrorLogger from "@/utils/useErrorLogger";

type AppointmentDropdownProps = {
  appointmentId: string;
  reviewStatus: ReviewStatus;
  appointmentStatus: AppointmentStatus;
};

function AppointmentDropdown({
  appointmentId,
  reviewStatus,
  appointmentStatus,
}: AppointmentDropdownProps) {
  const {
    cherryIntegrationV1Enabled,
    overrideApptCommsV1Enabled,
    multiProviderAppointmentsV1Enabled,
  } = useFeatureFlags();
  const isEligibleToRequestScripts = useIsEligibleToRequestScripts();
  const { medspa, hasFeaturePermission, hasUrlPermission } = useUser();
  const {
    query: { tab },
    push,
  } = useRouter();
  const logError = useErrorLogger();
  const { getConfirm } = useConfirm();
  const isMobile = useMoxieMediaQuery("ipad-hor", "down");

  const { data } = useCurrentAppointmentDropdownFragment(appointmentId);

  const [getChargeFeeData, { data: chargeFeeData }] =
    useAppointmentChargeFeeAdditionalInfoLazyQuery({
      variables: {
        appointmentId,
      },
    });

  const cancelAppointment = useCancelAppointment(appointmentId);

  const chargeLateCancellationFee = useChargeLateCancellationFee(appointmentId);

  const [telehealthFormType, setTelehealthFormType] = useState<"add" | "edit">(
    "add"
  );
  const [telehealthModalOpen, setTelehealthModalOpen] = useState(false);
  const [isEditAppointmentDrawerOpen, setIsEditAppointmentDrawerOpen] =
    useState(false);

  const [removeTelehealthDetails] =
    useDeleteTelehealthAppointmentDetailsMutation();

  const hasCardsOnFile =
    chargeFeeData?.appointmentByPk?.client.stripeData.stripeCardsList.length >
    0;
  const cancellationFeePolicyEnabled =
    chargeFeeData?.appointmentByPk?.medspa.configuration
      .cancellationFeePolicyEnabled;
  const isAlreadyInPaymentFlow =
    data?.invoice && data.invoice.status !== InvoiceStatus.DRAFT;
  const clientId = data?.client.id;

  const isEditable = isAppointmentActive(appointmentStatus);
  const canCompleteAppointment = hasFeaturePermission(
    FeaturePermission.COMPLETE_APPOINTMENT
  );

  const isChartSigned = data?.lastSignature?.length > 0;

  const { actions: chartDropdownItems, dialogComponent } =
    useChartReviewActions(
      appointmentId,
      appointmentStatus,
      reviewStatus,
      isChartSigned,
      data?.invoice?.id
    );

  const appointmentStatusActions = useAppointmentStatusActions(
    appointmentId,
    appointmentStatus,
    isEditable,
    canCompleteAppointment,
    data?.invoice
  );

  const {
    isOverrideCommunicationModalOpen,
    setIsOverrideCommunicationModalOpen,
    apptCommunicationState,
    handleSaveCommunicationOverride,
    editableChannels,
    setApptCommunicationState,
  } = useEditApptComms(appointmentId, data?.invoice?.id, appointmentStatus);

  const cherryPromotionEnabled =
    data?.medspa?.configuration?.cherryPromotionEnabled;

  const status = getCherryApplicationStatus(
    data?.client?.currentCherryApplicationRecord?.[0]
      ?.status as ClientCherryApplicationRecordStatus,
    data?.client?.currentCherryApplicationRecord?.[0]?.expiresAt,
    data?.client?.interestedInPaymentPlans
  );

  const paymentPlansActions = usePaymentPlansDropdownMenuItems(
    data?.client as ClientData,
    status
  );

  const paymentPlansDropdownItems =
    cherryIntegrationV1Enabled && cherryPromotionEnabled
      ? paymentPlansActions
      : [];

  const packageSuggestionActions = usePackageSuggestionsDropdownMenuItems(
    data?.client.id
  );

  const pendingReview = data?.client?.pendingGfeReview?.[0];
  const lastReviewId = data?.client?.lastGfeReview?.[0]?.id;
  const backUrl = `/${medspa}/appointments/${appointmentId}/${tab ? tab : "overview"}`;

  const startGfeReviewItem = useStartReviewDropdownItem(
    pendingReview as { id: string; asyncReviewedAt?: string },
    lastReviewId,
    clientId,
    backUrl
  );

  const lateCancellationHours =
    chargeFeeData?.appointmentByPk?.medspa.configuration
      ?.lateCancellationAdvanceHours;

  const isLateCancellation = getIsLateCancellation(
    data?.startTime,
    lateCancellationHours
  );

  const getCancelAppointmentLabel = () => {
    return isLateCancellation
      ? "Late cancel appointment"
      : "Cancel appointment";
  };

  const canEditAppointment = hasFeaturePermission(
    FeaturePermission.EDIT_APPOINTMENT
  );
  const canManageScripts = hasFeaturePermission(
    FeaturePermission.MANAGE_SCRIPT_REQUESTS
  );
  const canManageTelehealthAppointmentDetails = hasFeaturePermission(
    FeaturePermission.MANAGE_TELEHEALTH_APPOINTMENT_DETAILS
  );

  const addAdverseReactionUrl = `/${medspa}/appointments/${appointmentId}/adverse-reactions/new`;
  const canAddAdverseReaction = hasUrlPermission(addAdverseReactionUrl);

  const hasTelehealthDetails = !!data.telehealthAppointmentDetails;
  const isAppointmentTelehealth = data?.isAppointmentTelehealth;

  const handleRemoveTelehealthDetails = async () => {
    const confirm = await getConfirm(
      REMOVE_TELEHEALTH_DETAILS_CONFIRM_MODAL_CONTENT
    );
    if (!confirm) return;

    const toastId = toast.loading("Removing telehealth details...");

    try {
      await removeTelehealthDetails({
        variables: {
          appointmentId,
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({
              __typename: "appointment",
              id: appointmentId,
            }),
            fields: {
              telehealthAppointmentDetails: () => undefined,
            },
          });
        },
      });

      toast.success("Telehealth details have been removed successfully!", {
        id: toastId,
      });
    } catch (e) {
      logError(e);
      toast.error("An error occurred while removing telehealth details", {
        id: toastId,
      });
    }
  };

  const telehealthDetailsInitData = data.telehealthAppointmentDetails && {
    ...data.telehealthAppointmentDetails,
    appointmentType: data.telehealthAppointmentDetails
      .appointmentType as TelehealthType,
  };

  const items: DropdownMenuItems = [
    ...appointmentStatusActions,
    startGfeReviewItem,
    ...chartDropdownItems,
    ...paymentPlansDropdownItems,
    {
      component: "Request Patient Specific Script",
      onClick: () =>
        push(
          `/${medspa}/weight-loss-order/rn-order?client=${clientId}&appointment=${appointmentId}`
        ),
      disabled: !(isEligibleToRequestScripts && canManageScripts),
    },
    {
      component: "Edit client comms for appointment",
      onClick: () => setIsOverrideCommunicationModalOpen(true),
      disabled:
        !overrideApptCommsV1Enabled || !(isEditable && canEditAppointment),
    },
    {
      component: "Edit appointment",
      onClick: () => {
        if (multiProviderAppointmentsV1Enabled && !isMobile) {
          setIsEditAppointmentDrawerOpen(true);
        } else {
          push(`/${medspa}/appointments/${appointmentId}/edit`);
        }
      },
      disabled: !(isEditable && canEditAppointment),
    },
    ...(isAppointmentTelehealth
      ? [
          {
            component: "Add telehealth details",
            onClick: () => {
              setTelehealthFormType("add");
              setTelehealthModalOpen(true);
            },
            disabled:
              hasTelehealthDetails || !canManageTelehealthAppointmentDetails,
          },
          {
            component: "Edit telehealth details",
            onClick: () => {
              setTelehealthFormType("edit");
              setTelehealthModalOpen(true);
            },
            disabled:
              !hasTelehealthDetails || !canManageTelehealthAppointmentDetails,
          },
          {
            component: "Remove telehealth details",
            onClick: handleRemoveTelehealthDetails,
            disabled:
              !hasTelehealthDetails || !canManageTelehealthAppointmentDetails,
          },
        ]
      : []),
    // This is a default one, it could be either "Cancel appointment" or "Late cancel Appointment"
    {
      component: (
        <LazyItem
          isLoading={!chargeFeeData}
          label={getCancelAppointmentLabel()}
        />
      ),
      onClick: () =>
        chargeFeeData && cancelAppointment({ skipFeeChargeAttempt: false }),
      disabled: !(isEditable && canEditAppointment),
    },
    // This one appears only in pair with "Late cancel appointment" as an option for providers to mark as Cancelled while in late cancellation window
    {
      component: (
        <LazyItem isLoading={!chargeFeeData} label="Cancel appointment" />
      ),
      onClick: () =>
        chargeFeeData && cancelAppointment({ skipFeeChargeAttempt: true }),
      disabled: !(isEditable && isLateCancellation && canCompleteAppointment),
    },
    {
      component: (
        <LazyItem
          isLoading={!chargeFeeData}
          label="Charge late cancellation fee"
        >
          {cancellationFeePolicyEnabled && !hasCardsOnFile && (
            <Tooltip title="Client has no card on file">
              <IconButton sx={{ p: 0 }}>
                <WarningCircleIcon color={ERROR_PRIMARY} size="24px" />
              </IconButton>
            </Tooltip>
          )}
        </LazyItem>
      ),
      onClick: () => chargeFeeData && chargeLateCancellationFee(),
      disabled:
        !(
          appointmentStatus === AppointmentStatus.CANCELLED_LATE &&
          canCompleteAppointment &&
          !isAlreadyInPaymentFlow
        ) ||
        (chargeFeeData && !cancellationFeePolicyEnabled),
    },
    {
      component: "Report Adverse Reaction",
      onClick: () => push(addAdverseReactionUrl),
      disabled: !(
        canAddAdverseReaction &&
        ![
          AppointmentStatus.CANCELLED_LATE,
          AppointmentStatus.CANCELLED,
          AppointmentStatus.NO_SHOW,
        ].includes(appointmentStatus)
      ),
      divider: packageSuggestionActions.length > 0,
    },
    ...packageSuggestionActions,
  ];

  const menuItems = items.filter((item) => !item.disabled);
  const showDropdownMenu = menuItems.length > 0;

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      {showDropdownMenu && (
        <DropdownMenu
          type={null}
          onMenuOpen={() => getChargeFeeData()}
          menuItems={menuItems}
          buttonVariant="icon"
          icon={
            <MenuHorizontalIcon color={PRIMARY} size={24} strokeWidth={3} />
          }
        />
      )}
      {dialogComponent}
      <TelehealthDetailsDialog
        formType={telehealthFormType}
        appointmentId={appointmentId}
        initData={telehealthDetailsInitData}
        isOpen={telehealthModalOpen}
        onClose={() => setTelehealthModalOpen(false)}
      />

      {overrideApptCommsV1Enabled && (
        <CommunicationOverrideModal
          isOpen={isOverrideCommunicationModalOpen}
          onCancel={() => setIsOverrideCommunicationModalOpen(false)}
          clientName={data?.client.fullName}
          communicationState={apptCommunicationState}
          setCommunicationState={setApptCommunicationState}
          onSave={handleSaveCommunicationOverride}
          editableChannels={editableChannels}
        />
      )}
      {isEditAppointmentDrawerOpen && (
        <MultiProviderAppointmentDrawer
          appointmentId={appointmentId}
          open={isEditAppointmentDrawerOpen}
          onClose={() => setIsEditAppointmentDrawerOpen(false)}
          isEditing
        />
      )}
    </Box>
  );
}

function LazyItem({
  isLoading,
  label,
  children,
}: {
  isLoading: boolean;
  label: string;
  children?: ReactNode;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Typography variant="paragraphSmall">{label}</Typography>

      {isLoading ? (
        <CircularProgress size="16px" color="secondary" />
      ) : (
        children
      )}
    </Stack>
  );
}

export default AppointmentDropdown;
